/* -------------------------------------------------------------------------- */
/* The accounting timezone — one value set, shared by the API and the frontends */
/*                                                                            */
/* This is not a formatting preference. Once fiscal periods are generated in   */
/* the branch's timezone (IBA-118), the timezone DEFINES WHEN THE ACCOUNTING   */
/* MONTH BEGINS. A church in Kigali (UTC+2) has a January that starts at       */
/* 2025-12-31T22:00Z. Get the zone wrong and every period boundary moves.      */
/*                                                                            */
/* It was validated by REGEX — `^[A-Za-z]+(?:[/_][A-Za-z]+)*$` — which happily */
/* accepts `Foo/Bar` and `Africa/Kigalli`. A typo'd zone makes `date-fns-tz`   */
/* return an Invalid Date, and once posting depends on it that takes down ALL  */
/* posting for the branch. A fixed value set is an enum (project rule), and    */
/* here the rule is load-bearing rather than stylistic.                        */
/* -------------------------------------------------------------------------- */

/** The timezones Ibada Cloud supports. Extending this is a deliberate act. */
export enum AccountingTimezone {
  /* The home market. */
  AfricaKigali = "Africa/Kigali",
  AfricaLubumbashi = "Africa/Lubumbashi",
  AfricaKinshasa = "Africa/Kinshasa",
  AfricaBujumbura = "Africa/Bujumbura",
  AfricaNairobi = "Africa/Nairobi",
  AfricaKampala = "Africa/Kampala",
  AfricaDarEsSalaam = "Africa/Dar_es_Salaam",
  AfricaJohannesburg = "Africa/Johannesburg",
  AfricaLagos = "Africa/Lagos",
  AfricaAbidjan = "Africa/Abidjan",
  /* These observe DST. Period boundaries must be derived from the next period's start, never from
   * `+1 month − 1ms` on a single computed offset, or they gain an hour-long gap or overlap twice a year. */
  EuropeParis = "Europe/Paris",
  EuropeBrussels = "Europe/Brussels",
  EuropeLondon = "Europe/London",
  AmericaNewYork = "America/New_York",
  AmericaToronto = "America/Toronto",
  Utc = "UTC",
}

/** The default when nothing is set. Matches the `Branch.timezone` / `Workspace.timezone` schema default. */
export const DEFAULT_ACCOUNTING_TIMEZONE = AccountingTimezone.AfricaKigali;

/** Whether a raw string is a timezone this system actually supports. */
export const isSupportedTimezone = (value: string): value is AccountingTimezone =>
  Object.values(AccountingTimezone).includes(value as AccountingTimezone);

/**
 * Coerce a stored value to a supported timezone, falling back to the default.
 *
 * The fallback is DELIBERATE and narrow: it exists so that legacy or hand-edited data can never make a date
 * *unresolvable* and take down posting. It is not a licence to accept junk at the boundary — writes are
 * validated against the enum, so a value only reaches here if it was written before that validation existed.
 */
export const toAccountingTimezone = (value: string | null | undefined): AccountingTimezone =>
  value && isSupportedTimezone(value) ? value : DEFAULT_ACCOUNTING_TIMEZONE;
